export const UPDATE_STATES = Object.freeze({
  IDLE: 'IDLE',
  CHECKING: 'CHECKING',
  UPDATE_AVAILABLE: 'UPDATE_AVAILABLE',
  APPLYING_UPDATE: 'APPLYING_UPDATE',
  RELOADING: 'RELOADING',
  ERROR: 'ERROR',
});

function read(storage, key) {
  try {
    return storage?.getItem(key) || '';
  } catch {
    return '';
  }
}

function write(storage, key, value) {
  try {
    storage?.setItem(key, value);
  } catch {
    // Storage is optional; the in-memory state still prevents duplicate prompts.
  }
}

export function compareUpdateVersions(left = '', right = '') {
  const leftParts = String(left).split('.').map((part) => Number.parseInt(part, 10));
  const rightParts = String(right).split('.').map((part) => Number.parseInt(part, 10));
  const length = Math.max(leftParts.length, rightParts.length);
  for (let index = 0; index < length; index += 1) {
    const leftPart = Number.isFinite(leftParts[index]) ? leftParts[index] : 0;
    const rightPart = Number.isFinite(rightParts[index]) ? rightParts[index] : 0;
    if (leftPart !== rightPart) return leftPart - rightPart;
  }
  return 0;
}

export function hasNewerPublishedRelease(loadedVersion = '', publishedVersion = '') {
  return Boolean(loadedVersion && publishedVersion && compareUpdateVersions(publishedVersion, loadedVersion) > 0);
}

export function getUpdateStorageKeys(userId) {
  const scope = String(userId || 'anonymous');
  return {
    lastSeen: `rbh:update:last-seen:${scope}`,
    dismissed: `rbh:update:dismissed:${scope}`,
    applied: `rbh:update:applied:${scope}`,
  };
}

export function getChangelogUpdateDecision({ userId, latestVersion, acknowledgedVersion = '', storage = globalThis.localStorage }) {
  if (!userId || !latestVersion) return { state: UPDATE_STATES.IDLE };
  const keys = getUpdateStorageKeys(userId);
  const storedLastSeen = read(storage, keys.lastSeen) || read(storage, `rbh:changelog:lastSeen:${userId}`);
  const lastSeen = compareUpdateVersions(acknowledgedVersion, storedLastSeen) > 0
    ? String(acknowledgedVersion)
    : storedLastSeen;
  const dismissed = read(storage, keys.dismissed);
  const applied = read(storage, keys.applied);
  const isNewer = compareUpdateVersions(latestVersion, lastSeen) > 0;
  if (!isNewer || dismissed === latestVersion || applied === latestVersion) {
    return { state: UPDATE_STATES.IDLE, version: latestVersion, lastSeenVersion: lastSeen };
  }
  return { state: UPDATE_STATES.UPDATE_AVAILABLE, version: latestVersion, lastSeenVersion: lastSeen, keys };
}

export function getUnseenChangelogEntries(entries = [], lastSeenVersion = '') {
  const sortedEntries = Array.isArray(entries) ? entries : [];
  if (!lastSeenVersion) return sortedEntries.slice(0, 1);
  return sortedEntries.filter((entry) => compareUpdateVersions(entry?.version, lastSeenVersion) > 0);
}

export function dismissUpdatePrompt({ userId, version, storage = globalThis.localStorage }) {
  if (!userId || !version) return;
  write(storage, getUpdateStorageKeys(userId).dismissed, String(version));
}

export function applyUpdatePrompt({ userId, version, storage = globalThis.localStorage }) {
  if (!userId || !version) return;
  const keys = getUpdateStorageKeys(userId);
  write(storage, keys.applied, String(version));
  write(storage, keys.lastSeen, String(version));
}
